import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'react-hot-toast';
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';

import { ProtectedRoute } from './components/auth/ProtectedRoute';
import ErrorBoundary from './components/common/ErrorBoundary';
import Layout from './components/layout/Layout';
import { AuthProvider } from './contexts/AuthContext';
import { DensityProvider } from './contexts/DensityContext';
import { PreferencesProvider } from './contexts/PreferencesContext';

// Pages
import LoginPage from './pages/auth/LoginPage';
import CreditApplicationsPage from './pages/crm/CreditApplicationsPage';
import CRMDashboard from './pages/crm/CRMDashboard';
import CustomerDetailPage from './pages/customers/CustomerDetailPage';
import CustomersPage from './pages/customers/CustomersPage';
import CustomerWorkspacePage from './pages/customers/CustomerWorkspacePage';
import DashboardPage from './pages/dashboard/DashboardPage';
import DealCalculatorPage from './pages/deals/DealCalculatorPage';
import DealDetailPage from './pages/deals/DealDetailPage';
import DealsPage from './pages/deals/DealsPage';
import GPSDashboard from './pages/gps/GPSDashboard';
import CarLotPilotLandingPage from './pages/marketing/CarLotPilotLandingPage';
import ProfilePage from './pages/profile/ProfilePage';
import SettingsPage from './pages/settings/SettingsPage';
import DecisionsPage from './pages/tracker/DecisionsPage';
import EnvHealthPage from './pages/tracker/EnvHealthPage';
import ErrorsPage from './pages/tracker/ErrorsPage';
import PlansPage from './pages/tracker/PlansPage';
import RoadmapPage from './pages/tracker/RoadmapPage';
import RunbookPage from './pages/tracker/RunbookPage';
import SuperTrackerPage from './pages/tracker/SuperTrackerPage';
import TasksPage from './pages/tracker/TasksPage';
import UsersPage from './pages/users/UsersPage';
import InventoryCostsPage from './pages/inventory-costs/InventoryCostsPage';
import StockingGuidePage from './pages/vehicles/StockingGuidePage';
import VehicleDetailPage from './pages/vehicles/VehicleDetailPage';
import VehiclesPage from './pages/vehicles/VehiclesPage';

// BHPH Management Pages (Phase 6)
import BHPHAccountDetailPage from './pages/bhph/BHPHAccountDetailPage';
import BHPHAccountsPage from './pages/bhph/BHPHAccountsPage';
import BHPHDashboardPage from './pages/bhph/BHPHDashboardPage';
import CollectionsWorklistPage from './pages/bhph/CollectionsWorklistPage';
import LateFeeManagementPage from './pages/bhph/LateFeeManagementPage';
import PaymentProcessingPage from './pages/bhph/PaymentProcessingPage';
import PortalRequestsInboxPage from './pages/bhph/PortalRequestsInboxPage';

// Mobile Pages
import MobilePhotoCapture from './pages/mobile/MobilePhotoCapture';
import MobileVehiclePage from './pages/mobile/MobileVehiclePage';
import MobileVINScanner from './pages/mobile/MobileVINScanner';

// Public Website
import PublicWebsitePage from './pages/PublicWebsitePage';
import { getDomainSurface } from './utils/domainRouting';

// Document Center
import DocumentCenterPage from './pages/documents/DocumentCenterPage';

// Audit Logs
import AuditLogPage from './pages/audit/AuditLogPage';

/**
 * Root Router to handle domain-based routing.
 * Shows Public Website on root domains, and CRM Dashboard on subdomains.
 */
const RootPathRouter = () => {
  const domainSurface = getDomainSurface(window.location.hostname);

  if (domainSurface === 'product') {
    return <CarLotPilotLandingPage />;
  }

  // If we're NOT on an app-specific subdomain, show the public-facing dealership website.
  // This serves the public website on unitedautobrokers.online and other tenant domains.
  if (domainSurface === 'public-site') {
    return <PublicWebsitePage />;
  }

  // Otherwise, render the Protected CRM Layout (for staging and app subdomains)
  return (
    <ProtectedRoute>
      <Layout />
    </ProtectedRoute>
  );
};

const HomeIndexRedirect = () => {
  if (getDomainSurface(window.location.hostname) === 'app') {
    return <Navigate to="/dashboard" replace />;
  }

  // This case should theoretically not be reached as RootPathRouter handles
  // the non-app domain case by rendering PublicWebsitePage directly.
  return null;
};

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <PreferencesProvider>
            <DensityProvider autoDetectScreenSize={true} persistToLocalStorage={true}>
              <Router
                future={{
                  v7_startTransition: true,
                  v7_relativeSplatPath: true,
                }}
              >
                <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
                  <ErrorBoundary>
                    <Routes>
                      {/* Public routes */}
                      <Route path="/login" element={<LoginPage />} />
                      <Route path="/website" element={<Navigate to="/website/preview" replace />} />
                      <Route path="/website/:subdomain/*" element={<PublicWebsitePage />} />

                      {/* Protected routes / Domain-aware entry */}
                      <Route path="/*" element={<RootPathRouter />}>
                        <Route index element={<HomeIndexRedirect />} />
                        <Route path="dashboard" element={<DashboardPage />} />

                        {/* Customer routes */}
                        <Route path="customers" element={<CustomersPage />} />
                        <Route path="customers/:id" element={<CustomerDetailPage />} />
                        <Route
                          path="customers/:id/workspace"
                          element={
                            <ErrorBoundary>
                              <CustomerWorkspacePage />
                            </ErrorBoundary>
                          }
                        />
                        <Route
                          path="customers/portal-requests"
                          element={<Navigate to="/bhph/portal-requests" replace />}
                        />

                        {/* Vehicle routes */}
                        <Route path="vehicles" element={<VehiclesPage />} />
                        <Route path="vehicles/:id" element={<VehicleDetailPage />} />
                        <Route path="vehicle/:vin" element={<VehicleDetailPage />} />

                        {/* Inventory routes */}
                        <Route
                          path="inventory/stocking-guide"
                          element={
                            <ErrorBoundary>
                              <StockingGuidePage />
                            </ErrorBoundary>
                          }
                        />
                        <Route
                          path="inventory/costs"
                          element={
                            <ProtectedRoute
                              roles={[
                                'system_admin',
                                'general_manager',
                                'finance_manager',
                                'sales_manager',
                              ]}
                            >
                              <ErrorBoundary>
                                <InventoryCostsPage />
                              </ErrorBoundary>
                            </ProtectedRoute>
                          }
                        />

                        {/* Deal routes */}
                        <Route
                          path="deals"
                          element={
                            <ErrorBoundary>
                              <DealsPage />
                            </ErrorBoundary>
                          }
                        />
                        <Route
                          path="deals/:id"
                          element={
                            <ErrorBoundary>
                              <DealDetailPage />
                            </ErrorBoundary>
                          }
                        />
                        <Route
                          path="calculator"
                          element={
                            <ErrorBoundary>
                              <DealCalculatorPage />
                            </ErrorBoundary>
                          }
                        />

                        {/* BHPH routes */}
                        <Route
                          path="bhph-accounts"
                          element={<Navigate to="/bhph/accounts" replace />}
                        />
                        <Route
                          path="bhph-dashboard"
                          element={<Navigate to="/bhph/dashboard" replace />}
                        />
                        <Route
                          path="bhph/dashboard"
                          element={
                            <ErrorBoundary>
                              <BHPHDashboardPage />
                            </ErrorBoundary>
                          }
                        />
                        <Route
                          path="bhph/accounts"
                          element={
                            <ErrorBoundary>
                              <BHPHAccountsPage />
                            </ErrorBoundary>
                          }
                        />
                        <Route
                          path="bhph/worklist"
                          element={
                            <ErrorBoundary>
                              <CollectionsWorklistPage />
                            </ErrorBoundary>
                          }
                        />
                        <Route
                          path="bhph/payment"
                          element={
                            <ErrorBoundary>
                              <PaymentProcessingPage />
                            </ErrorBoundary>
                          }
                        />
                        <Route
                          path="bhph/account/:accountId"
                          element={
                            <ErrorBoundary>
                              <BHPHAccountDetailPage />
                            </ErrorBoundary>
                          }
                        />
                        <Route
                          path="bhph/late-fees"
                          element={
                            <ErrorBoundary>
                              <LateFeeManagementPage />
                            </ErrorBoundary>
                          }
                        />
                        <Route
                          path="bhph/portal-requests"
                          element={
                            <ErrorBoundary>
                              <PortalRequestsInboxPage />
                            </ErrorBoundary>
                          }
                        />

                        {/* CRM routes */}
                        <Route
                          path="crm"
                          element={
                            <ErrorBoundary>
                              <CRMDashboard />
                            </ErrorBoundary>
                          }
                        />
                        <Route
                          path="credit-applications"
                          element={
                            <ErrorBoundary>
                              <CreditApplicationsPage />
                            </ErrorBoundary>
                          }
                        />

                        {/* GPS routes — not in the sidebar nav (P5-D1), but
                            kept reachable: GPS is core BHPH functionality. */}
                        <Route
                          path="gps"
                          element={
                            <ErrorBoundary>
                              <GPSDashboard />
                            </ErrorBoundary>
                          }
                        />

                        {/* User management routes */}
                        <Route
                          path="users"
                          element={
                            <ErrorBoundary>
                              <UsersPage />
                            </ErrorBoundary>
                          }
                        />

                        {/* Profile route */}
                        <Route
                          path="profile"
                          element={
                            <ErrorBoundary>
                              <ProfilePage />
                            </ErrorBoundary>
                          }
                        />

                        {/* Settings routes */}
                        <Route
                          path="settings"
                          element={
                            <ErrorBoundary>
                              <SettingsPage />
                            </ErrorBoundary>
                          }
                        />

                        {/* Document Center */}
                        <Route
                          path="documents"
                          element={
                            <ErrorBoundary>
                              <DocumentCenterPage />
                            </ErrorBoundary>
                          }
                        />

                        {/* Audit Logs */}
                        <Route
                          path="audit-logs"
                          element={
                            <ProtectedRoute roles={['developer']}>
                              <ErrorBoundary>
                                <AuditLogPage />
                              </ErrorBoundary>
                            </ProtectedRoute>
                          }
                        />

                        {/* SuperTracker routes */}
                        <Route
                          path="tracker"
                          element={
                            <ProtectedRoute roles={['developer']}>
                              <SuperTrackerPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="tracker/roadmap"
                          element={
                            <ProtectedRoute roles={['developer']}>
                              <RoadmapPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="tracker/tasks"
                          element={
                            <ProtectedRoute roles={['developer']}>
                              <TasksPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="tracker/errors"
                          element={
                            <ProtectedRoute roles={['developer']}>
                              <ErrorsPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="tracker/decisions"
                          element={
                            <ProtectedRoute roles={['developer']}>
                              <DecisionsPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="tracker/health"
                          element={
                            <ProtectedRoute roles={['developer']}>
                              <EnvHealthPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="tracker/runbooks"
                          element={
                            <ProtectedRoute roles={['developer']}>
                              <RunbookPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="tracker/plans"
                          element={
                            <ProtectedRoute roles={['developer']}>
                              <PlansPage />
                            </ProtectedRoute>
                          }
                        />
                      </Route>

                      {/* Mobile routes - Protected but without full Layout */}
                      <Route
                        path="/mobile/vehicle/:id"
                        element={
                          <ProtectedRoute>
                            <MobileVehiclePage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/mobile/vehicle/:id/photos"
                        element={
                          <ProtectedRoute>
                            <MobilePhotoCapture />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/mobile/scan"
                        element={
                          <ProtectedRoute>
                            <MobileVINScanner />
                          </ProtectedRoute>
                        }
                      />

                      {/* Catch all route */}
                      <Route path="*" element={<Navigate to="/dashboard" replace />} />
                    </Routes>
                  </ErrorBoundary>
                </div>

                {/* Global toast notifications */}
                <Toaster
                  position="top-right"
                  toastOptions={{
                    duration: 4000,
                    style: {
                      background: '#363636',
                      color: '#fff',
                    },
                  }}
                />
              </Router>
            </DensityProvider>
          </PreferencesProvider>
        </AuthProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
